import { useRef } from 'react';

const HOLD_MS = 550;
const MOVE_PX = 10;

// Word under a screen point, using the browser caret APIs.
const wordAtPoint = (x, y) => {
  let node; let offset;
  if (document.caretPositionFromPoint) {
    const p = document.caretPositionFromPoint(x, y);
    node = p?.offsetNode; offset = p?.offset;
  } else if (document.caretRangeFromPoint) {
    const r = document.caretRangeFromPoint(x, y);
    node = r?.startContainer; offset = r?.startOffset;
  }
  if (!node || node.nodeType !== Node.TEXT_NODE) return '';
  const text = node.textContent || '';
  const isWord = (c) => /[\p{L}\p{M}'’-]/u.test(c);
  let s = offset; let e = offset;
  while (s > 0 && isWord(text[s - 1])) s -= 1;
  while (e < text.length && isWord(text[e])) e += 1;
  return text.slice(s, e).replace(/^['’-]+|['’-]+$/g, '');
};

/**
 * Long-press (touch or mouse hold) on a word → onWord(word, meta).
 * Returns pointer handlers plus `consumeClick` so the follow-up click doesn't select the verse.
 */
export default function useLongPressWord(onWord) {
  const timer = useRef(null);
  const start = useRef(null);
  const fired = useRef(false);

  const clear = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };

  const bind = (meta) => ({
    onPointerDown: (e) => {
      if (e.button && e.button !== 0) return;
      fired.current = false;
      start.current = { x: e.clientX, y: e.clientY };
      clear();
      timer.current = setTimeout(() => {
        const word = wordAtPoint(start.current.x, start.current.y);
        if (word) {
          fired.current = true;
          window.getSelection?.()?.removeAllRanges();
          if (navigator.vibrate) navigator.vibrate(15);
          onWord(word, meta);
        }
      }, HOLD_MS);
    },
    onPointerMove: (e) => {
      if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > MOVE_PX) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onContextMenu: (e) => { if (fired.current) e.preventDefault(); },
  });

  // Call at the top of the row's onClick; true means "this click ended a long-press".
  const consumeClick = () => {
    if (!fired.current) return false;
    fired.current = false;
    return true;
  };

  return { bind, consumeClick };
}
